import AutomationToolBenefitsDesktop from "@components/AutomationToolBenefitsDesktop";
import AutomationToolBenefitsMobile, { AutomationToolBenefitsProps } from "@components/AutomationToolBenefitsMobile";
import PaymentProcessCard, { PaymentSectionDataProps } from "@components/integration/PaymentProcessCard";
import PaymentStepsAccordion, { PaymentProcess } from "@components/integration/PaymentStepsAccordion";
import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";

import React from "react";

const herosectionData: HerosectionProps = {
    chipTitle: "WhatsApp automation tool",
    title: "WhatsApp Flows — AI-Assisted In-Chat Experiences",
    description:
        "Improve lead engagement, drive sales, and reduce support tickets for your business with an AI-powered WhatsApp automation tool. Scale your business without increasing your headcount.",
    primaryButtonText: "Try for free",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/whatsapp-flow-hero.png",
};

const featureInfo1: FeatureInfo = {
    mainTitle: "Build WhatsApp forms in as easy as 3 clicks",
    subtitle2: "With Gallabox’s intuitive WhatsApp flows platform, businesses can:",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/whatsapp-form.png",
    featureInfoList: ["Build their own forms in minutes.", "Use drag-and-drop widgets to tailor experiences.", "Deploy industry-specific templates in a single click."],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Collect data and deliver actionable insights in real-time",
    subtitle2: "Gallabox helps you collect, store, streamline, and retrieve data easily with just a few clicks. This enables businesses to:",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/real-time.png",
    featureInfoList: [
        "Hold a centralized database of every user interaction.",
        "Drive powerful business outcomes.",
        "Build better lead qualification and data enrichment funnels.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Automate responses and build flows that convert",
    subtitle2: "Create intelligent flows and subflows that work to optimize lead nurturing and conversion.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/build-flows.png",
    featureInfoList: ["Direct user journeys.", "Segment users to drive better nurturing.", "Use automation to refine user engagement."],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Integrate with all your office tools",
    subtitle2: "Build the perfect ecosystem of interconnected solutions, where your data is always updated in real-time, and stays true across the organization",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/integrate.png",
    featureInfoList: [
        "Push your data into your favourite CRM, ERP, eCommerce platform, and appointment scheduling tools. ",
        "Have a real-time, centralized source of truth.",
        "Webhooks and APIs that connect with your in-house tools.",
    ],
};

const featureInfo5: FeatureInfo = {
    mainTitle: "One click industry-specific flow templates",
    subtitle2: "Want to deploy your WhatsApp bots quickly? Use the WhatsApp flows library to select and execute any template in a single click.",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/flow-template.png",
    featureInfoList: ["Industry-specific WhatsApp flows", "Easily customizable", "Deploy and test in just a couple of minutes"],
};

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/thread-bucket-studio/thread-bucket-studio.png",
        chiptitle: "E-COMMERCE",
        title: "Why Thread Bucket Studio chose Gallabox over Wati for seamless team communication",
        statsInfoData: [
            { percentage: "68%", label: "Higher business growth" },
            { percentage: "50%", label: "Rise in employee productivity" },
            { percentage: "30%", label: "Growth in customer base" },
        ],
        url: "/case-study/thread-bucket-studio",
    },
];

const faqData: FAQSectionProps = {
    title: "FAQs frequently asked questions",
    faqItems: [
        {
            question: "Can I create automated workflows with the WhatsApp Flows Builder?",
            answer: "Yes, Gallabox’s custom WhatsApp Flows builder helps teams build custom flows for their lead generation, appointment scheduling, feedback surveys, and more. You can also use pre-existing templates and create a custom automated chat flow from scratch.",
        },
        {
            question: "Is the WhatsApp Flows Builder easy to use for beginners?",
            answer: "Yes. With Gallabox, you get an easy-to-use flow builder that’s tailored towards teams that might not necessarily be tech mavericks. This is why we have a simple yet intuitive no-code framework, where all you have to do is drag and drop a few widgets and connect them to make things work as you want. It’s as simple as that.",
        },
        {
            question: "Does the WhatsApp Flows Builder support multi-step workflows?",
            answer: "Yes. Multi-step workflows are certainly possible with Gallabox’s WhatsApp Flow Builder. You will be able to set up action-based flows, where each action can lead the user down a certain set of predefined steps.",
        },
        {
            question: "Can I use conditional logic in WhatsApp Flows?",
            answer: "Yes. Conditional flows can definitely be set up through custom flows, where you will be able to set time-based/alert-based triggers to better guide your users’ journeys. Our clients at Gallabox use it for a number of use cases, including appointment reminders, service updates, and even simple order delivery updates.",
        },
        {
            question: "Does the WhatsApp Flows Builder allow for personalized messages?",
            answer: "Absolutely. With WhatsApp Flows, businesses can create and deliver tailored customer experiences through WhatsApp. You can leverage personalized offers and rewards to drive better engagement for your business.",
        },
        {
            question: "Can I integrate WhatsApp Flows Builder with other platforms and CRMs?",
            answer: "Yes. Gallabox supports a host of different connectors that work with your favourite CRMs, ERPs, and meeting calendars. You also have access to webhooks and APIs to connect  your in-house applications and make data transfer seamless. You can even use third-party integrations like Zapier and Make to connect to your custom solutions.",
        },
        {
            question: "Can I test WhatsApp flows before deploying them?",
            answer: "Definitely. We understand how critical WhatsApp flows can be to a business. This is why with Gallabox’s WhatsApp flow builder, you will be able to test the flow on your number with a single click. This makes sure your custom chat flows work as intended, and your customers don’t have to be the crash test dummies.",
        },
        {
            question: "What types of messages can I send using WhatsApp Flows (e.g., text, media, buttons)?",
            answer: "Yes. WhatsApp Flows support rich media, so you will be able to send out media and text campaigns to your customers without a hitch. But with Gallabox, we go a step further, and also enable users to include interactive chat flows using buttons, dropdown menus, and even toggles. This helps your business engage with your customers in a much more effective way.",
        },
        {
            question: "How can I automate customer support using the WhatsApp Flows Builder?",
            answer: (
                <p>
                    Customers use Gallabox to create interactive chat flows for their customer support by designing and implementing automated workflows to handle appointment
                    bookings, service availability, and even customer queries. Take
                    <a href="https://gallabox.com/case-study/dubai-housekeeping" className="px-1 text-blue-800" rel="noreferrer" target="_blank">
                        Dubai Housekeeping
                    </a>{" "}
                    for example — Gallabox enables them to handle all their customer queries straight from WhatsApp through automated chatbots, while also providing support for
                    appointment booking, service availability and more, with zero manual intervention.
                </p>
            ),
        },
        {
            question: "Does the WhatsApp Flows Builder support message scheduling?",
            answer: "Yes. You can definitely use the WhatsApp Flows Builder to schedule and send timed message campaigns to your customers and prospects through WhatsApp.",
        },
        {
            question: "Can I track the performance of WhatsApp flows and analyze user engagement?",
            answer: "Yes. WhatsApp Business API tools like Gallabox help provide detailed insights into user engagement, with metrics like delivery rates, click-through rates, open rates, and more — all available in a single unified dashboard.",
        },
        {
            question: "Is there a limit to the number of flows I can create with the WhatsApp Flows Builder?",
            answer: "Yes. There are certain restrictions to the WhatsApp Flows builder that are put in place to help make Gallabox remain user-friendly and intuitive to the customer. Currently you will be able to add up to 8 components or widgets per screen, and each flow can have up to 8 screens.",
        },
        {
            question: "Is it possible to collaborate with my team when building WhatsApp flows?",
            answer: "Absolutely. With Gallabox, you get shared access, so multiple team members can work together to build and manage chat flows and WhatsApp bots. You can always reach out to our support for more information on this.",
        },
        {
            question: "Can I use WhatsApp Flows Builder to handle lead generation and nurturing?",
            answer: "Yes. With Gallabox’s WhatsApp Flows builder, businesses can streamline their lead generation efforts and drastically bring down acquisition costs. You can also use advanced marketing drip campaigns to nurture and move leads down the pipeline over WhatsApp.",
        },
    ],
};

const paymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/gallabox.png",
        title: "Get Gallabox",
        description: "Sign up for a free trial with Gallabox. Connect your WhatsApp business number and your business details to get started with a free account.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/select-template.png",
        title: "Select a Template",
        description: "Use one of our tried and tested pre-built WhatsApp flows templates, or build one from scratch. Deploy your templates in a single click.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/collect-data.png",
        title: "Sit back and collect data",
        description: "Once your WhatsApp flow has been built and deployed, collect the data and derive actionable insights for your marketing and sales campaigns.",
    },
];

const defaultFAQData: PaymentProcess = {
    title: "How to get started with WhatsApp Flows Builder",
    ProcessSteps: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/gallabox.png",
            title: "Get Gallabox",
            description: "Sign up for a free trial with Gallabox. Connect your WhatsApp business number and your business details to get started with a free account.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/select-template.png",
            title: "Select a Template",
            description: "Use one of our tried and tested pre-built WhatsApp flows templates, or build one from scratch. Deploy your templates in a single click.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/collect-data.png",
            title: "Sit back and collect data",
            description: "Once your WhatsApp flow has been built and deployed, collect the data and derive actionable insights for your marketing and sales campaigns.",
        },
    ],
};

const automationToolBenefitsData: AutomationToolBenefitsProps = {
    title: "Why teams use  WhatsApp Flows Builder",
    ProcessSteps: [
        {
            title: "Qualify more leads, faster",
            description:
                "Use Gallabox’s WhatsApp Flows solution to drive better engagement with your prospects and qualify them with better accuracy. With automated messaging and lead capture forms, it becomes very simple to qualify leads effortlessly for your business.",
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/qualify-leads.png",
        },
        {
            title: "Book more appointments",
            description:
                "Whether it’s scheduling a home repair service or helping users reserve a table at a restaurant, WhatsApp Flows helps businesses deploy beautiful, customizable forms that can make scheduling appointments a breeze.",
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/appointments.png",
        },
        {
            title: "Resolve issues and collect feedback swiftly",
            description:
                "With many helpful pre-built templates like support and feedback flows, businesses can use the WhatsApp Flows builder to quickly send out service updates and collect feedback on resolutions, thereby increasing customer engagement and satisfaction levels/scores.",
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/feedback-swiftly.png",
        },
    ],
};

const whatsappFlows: React.FC = () => {
    return (
        <NewLayout
            title="WhatsApp Flows — AI-Powered Forms, Surveys & In-Chat Apps | Gallabox"
            description="Build WhatsApp Flows with AI assistance. Lead capture forms, smart surveys, booking flows & payment collection — all inside the chat."
            canonical="https://gallabox.com/whatsapp-flows"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/whatsapp-flows/whatsap-flow-og.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-flows-image",
                heroOrOgImageDescription:
                    "Create eye-catching WhatsApp flows, share with potential and existing customers, and collect data to structure and derive actionable insights.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/whatsapp-flows/whatsapp-flow-hero.png",
                softwareFeatureName: "whatsapp-flows",
                faq: faqData.faqItems,
            }}
        >
            <Herosection herosection={herosectionData} />
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos />
            </section>
            <StatsInfo
                statsInfo={[
                    { percentage: "34%", label: "Reduction in lead follow-up time" },
                    { percentage: "53%", label: "Reduction in lead qualification costs" },
                    { percentage: "71%", label: "Increase in conversion rates" },
                ]}
            />
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <FeatureInfoCard {...featureInfo4} />
            <FeatureInfoCard {...featureInfo5} removeDivider />

            <AutomationToolBenefitsDesktop automationToolBenefits={automationToolBenefitsData} />

            <AutomationToolBenefitsMobile automationToolBenefits={automationToolBenefitsData} />

            <PaymentProcessCard title="How to get started with WhatsApp Flows Builder" paymentSectionData={paymentSectionData} backgroundColor="white" />

            <PaymentStepsAccordion paymentStepsData={defaultFAQData} />
            <div className="mt-20">
                <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            </div>
            <FAQSection faqData={faqData} />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection
                title="Start using WhatsApp Flows Builder for free"
                description="See why 5000+ customers believe in the power of WhatsApp automation through Gallabox"
                primaryButtonText="Get free trial"
            />
        </NewLayout>
    );
};

export default whatsappFlows;
